import { useParams } from '@tanstack/react-router';
import type { ReactNode } from 'react';
import { ErrorState, ReadOnlyBanner } from '@bb/ui';
import { usePermissions } from '../../access/usePermissions.ts';
import { AndroidChannelsTable } from './AndroidChannelsTable.tsx';
import { AppCapabilitiesSection } from './AppCapabilitiesSection.tsx';
import { CredentialsSection } from './CredentialsSection.tsx';
import { DefaultsForm } from './DefaultsForm.tsx';
import { IosCategoriesEditor } from './IosCategoriesEditor.tsx';
import { IosThreadsTable } from './IosThreadsTable.tsx';
import { problemText } from './problems.ts';
import { SdkConfigSnippet } from './SdkConfigSnippet.tsx';
import { usePushSettingsData } from './usePushSettingsData.ts';

function Section({ id, title, children }: { id: string; title: string; children: ReactNode }) {
  return (
    <section className="push-settings-page__section" aria-labelledby={id}>
      <h2 id={id}>{title}</h2>
      {children}
    </section>
  );
}

/**
 * Settings → Push settings (BB-77, PUSH-004): environment defaults of push, provider credentials of the apps (BB-72),
 * what each app declares, the Android channel / iOS category / thread registry and the config the apps paste in.
 * Everyone in the environment reads it; `integrations.manage` changes defaults, registry and credentials, `apps.manage`
 * app capabilities.
 */
export function PushSettingsPage() {
  const { environmentId } = useParams({ from: '/env/$environmentId/settings/push' });
  const { has } = usePermissions();
  const canManage = has('integrations.manage');
  const canEditApps = has('apps.manage');
  const data = usePushSettingsData(environmentId);
  const appsLoaded = (render: () => ReactNode) =>
    data.apps.isError ? <ErrorState title="Apps could not be loaded" message={problemText(data.apps.error)} onRetry={() => void data.apps.refetch()} /> : data.apps.data ? render() : <p aria-busy="true">Loading apps…</p>;

  return (
    <section className="push-settings-page">
      <h1>Push settings</h1>
      <p className="hint">What a push falls back to, how the apps reach FCM and APNs, and the channels, categories and threads the apps create on devices.</p>
      {!canManage && <ReadOnlyBanner>Push settings are changed by people with integrations.manage; app capabilities by apps.manage.</ReadOnlyBanner>}
      <Section id="push-defaults" title="Defaults">
        <DefaultsForm environmentId={environmentId} data={data} canEdit={canManage} />
      </Section>
      <Section id="push-credentials" title="Credentials">
        {canManage ? appsLoaded(() => <CredentialsSection environmentId={environmentId} apps={data.appsWithPush} canManage canTestSend={has('messages.test_send')} />) : <CredentialsSection environmentId={environmentId} apps={[]} canManage={false} canTestSend={false} />}
      </Section>
      <Section id="push-capabilities" title="App capabilities">
        {appsLoaded(() => <AppCapabilitiesSection environmentId={environmentId} apps={data.appsWithPush} canEdit={canEditApps} />)}
      </Section>
      <Section id="push-android-channels" title="Android notification channels">
        <AndroidChannelsTable environmentId={environmentId} data={data} canEdit={canManage} />
      </Section>
      <Section id="push-ios-categories" title="iOS notification categories">
        <IosCategoriesEditor environmentId={environmentId} data={data} canEdit={canManage} />
      </Section>
      <Section id="push-ios-threads" title="iOS threads">
        <IosThreadsTable environmentId={environmentId} data={data} canEdit={canManage} />
      </Section>
      <Section id="push-sdk-config" title="SDK configuration">
        {appsLoaded(() => <SdkConfigSnippet environmentId={environmentId} apps={data.appsWithPush} />)}
      </Section>
    </section>
  );
}
